<template>
  <v-dialog persistent v-model="opened" width="500">
    <v-card>
      <v-card-title>
        {{ $t("activities.edit.chat.title") }}
      </v-card-title>
      <v-card-subtitle class="search-item d-flex">
        <v-text-field
          outlined
          dense
          hide-details
          clearable
          :placeholder="$t('activities.edit.chat.search')"
          v-model="searchTerm"
        ></v-text-field>
        <v-menu :close-on-content-click="false" offset-y v-model="filterOptions">
          <template v-slot:activator="{ on }">
            <v-btn class="ml-2" icon v-on="on">
              <v-icon>mdi-filter</v-icon>
            </v-btn>
          </template>
          <v-card flat>
            <v-card-text>
              <v-select
                :items="teamFilter"
                item-text="label"
                item-value="id"
                :label="$t('activities.edit.chat.teamFilter')"
                outlined
                v-model="teamFilterId"
              ></v-select>
            </v-card-text>
          </v-card>
        </v-menu>
      </v-card-subtitle>
      <v-card-text class="pt-2">
        <v-list
          v-if="!loading"
          shaped
          class="mb-auto flex-grow-1 pa-2"
          dense
          id="participant-chat-log"
          style="overflow: auto"
        >
          <v-list-item class="chat-moderation-item" v-bind:key="item.id" v-for="item in messagesFiltered">
            <v-badge
              :content="
                item.participant.session.teamId ? item.participant.session.team.name : $t('activities.edit.chat.global')
              "
              left
              offset-x="65"
              offset-y="19"
              overlap
            >
              <v-list-item-avatar :color="item.participant.color" size="36">
                <span>{{ item.participant.initials }}</span>
              </v-list-item-avatar>
            </v-badge>
            <v-list-item-content>
              <v-list-item-title class="font-weight-medium" v-text="item.participant.username"></v-list-item-title>
              <v-list-item-subtitle
                class="font-weight-regular"
                v-text="item.message"
                style="white-space: normal"
              ></v-list-item-subtitle>
            </v-list-item-content>
            <v-list-item-action>
              <v-btn :loading="itemLoading" class="mx-2" dark icon raised color="error" @click="removeChatEntry(item)">
                <v-icon dark>mdi-chat-remove</v-icon>
              </v-btn>
            </v-list-item-action>
          </v-list-item>
          <v-list-item v-if="messages && messages.length === 0">
            <v-list-item-content>
              {{ $t("activities.edit.chat.empty") }}
            </v-list-item-content>
          </v-list-item>
        </v-list>
        <v-list v-if="loading">
          <v-list-item>
            <v-list-item-content>
              <v-progress-circular />
            </v-list-item-content>
          </v-list-item>
        </v-list>
      </v-card-text>
      <v-card-actions class="list-footer-sticky">
        <v-spacer></v-spacer>
        <v-btn :loading="loading" color="primary" @click.prevent="refresh()">
          {{ $t("activities.edit.chat.refresh") }}
        </v-btn>
        <v-btn :loading="loading" color="error" @click.prevent="removeAllFiltered()">
          <span v-if="!!!searchTerm">{{ $t("activities.edit.chat.removeAll") }}</span>
          <span v-if="!!searchTerm">{{ $t("activities.edit.chat.removeMany") }}</span>
        </v-btn>
        <v-btn color="secondary" outlined @click.prevent="close()">
          {{ $t("activities.edit.chat.close") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import i18n from "../../../plugins/i18n";

export default {
  name: "ActivitiesEditChatModal",
  data() {
    return {
      filterOptions: false,
      opened: false,
      currentItem: null,
      loading: false,
      messages: null,
      teamFilterData: null,
      itemLoading: false,
      searchTerm: "",
      teamFilterId: null,
      autoRefreshIntervalId: null,
    };
  },
  computed: {
    messagesFiltered() {
      if (!this.messages) return this.messages;
      let filtered = this.messages.filter((item) => {
        return (
          item.message.toLowerCase().indexOf(this.searchTerm.toLowerCase()) > -1 ||
          item.participant.username.toLowerCase().indexOf(this.searchTerm.toLowerCase()) > -1 ||
          item.participant.firstName.toLowerCase().indexOf(this.searchTerm.toLowerCase()) > -1 ||
          item.participant.lastName.toLowerCase().indexOf(this.searchTerm.toLowerCase()) > -1
        );
      });

      return filtered.filter((message) => {
        return !this.teamFilterId || message.participant.session.teamId === this.teamFilterId;
      });
    },
    teamFilter() {
      let filterData = [];
      if (this.teamFilterData) {
        this.teamFilterData.teamsMessages.forEach((value) => {
          filterData.push({
            label: `${value.name} (${value.chatCount})`,
            id: value.id,
          });
        });

        filterData.unshift({
          label: `${i18n.t("activities.edit.chat.all")} (${this.teamFilterData.totalMessagesCount})`,
          id: null,
        });
      }

      return filterData;
    },
  },
  methods: {
    open(item) {
      this.currentItem = item;
      this.opened = true;
      this.refresh();
      this.scheduleAutoRefresh();
    },
    close() {
      this.opened = false;
      this.currentItem = null;
      this.cancelAutoRefresh();
    },
    async removeChatEntry(entry) {
      this.itemLoading = true;
      await this.$axios.delete(`admin/chat/${entry.id}`);
      await this.loadData();
      this.itemLoading = false;
      return true;
    },
    async refresh() {
      this.loading = true;
      this.loadData();
      this.loading = false;
    },
    async removeAllFiltered() {
      this.loading = true;
      const chatIds = this.messagesFiltered.map((item) => item.id);
      await this.$axios.post(`admin/chat/removeManyChatItems`, {
        chatIds,
      });
      this.searchTerm = "";
      this.refresh();
    },
    async loadData() {
      this.messages = await this.$axios.get(`event/activities/${this.currentItem.id}/getChatMessages`);
      this.teamFilterData = await this.$axios.get(`event/activities/${this.currentItem.id}/getChatMessagesTeamFilter`);

      this.scrollChatListToBottom();
    },
    scheduleAutoRefresh() {
      if (!this.autoRefreshIntervalId) this.autoRefreshIntervalId = setInterval(this.refresh, 10 * 1000);
    },
    cancelAutoRefresh() {
      clearInterval(this.autoRefreshIntervalId);
      this.autoRefreshIntervalId = null;
    },
    scrollChatListToBottom() {
      const element = document.getElementsByClassName("v-dialog--active")[0];
      if (element) element.scrollTop = element.scrollHeight - element.clientHeight;
    },
  },
};
</script>

<style scoped lang="scss">
.chat-moderation-item {
  cursor: pointer;
  transition: background 300ms;
  .v-list-item__action {
    opacity: 0;
    transition: opacity 300ms;
  }
  &:hover {
    background: #eeeeee;
    .v-list-item__action {
      opacity: 1;
    }
  }
}
.list-footer-sticky {
  position: sticky;
  bottom: 0;
  background: #ffffffcc;
}
.search-item {
  margin-top: 0 !important;
  padding-top: 5px !important;
  padding-bottom: 5px !important;
  position: sticky;
  top: 0;
  z-index: 1;
  background: #ffffff;
}
</style>
